import { currentUid, readStorage, clearSession } from "@/utils/storage.js";
import { toast } from "@/utils/feedback.js";

const runtime = window.__ZWJL_SITE__ || window.__ZWJL_CONFIG__ || {};
const localDevelopment = import.meta.env.DEV;
export const apiBase = localDevelopment
  ? "/__api/index.php?route="
  : runtime.apiBase || "/api/index.php?route=";
export function getApiUrl(action, params = {}) {
  const url = new URL(apiBase + action, location.origin);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null)
      url.searchParams.set(key, String(value));
  });
  // OAuth redirects must go through the real API host in development.
  if (localDevelopment && action === "api/wechatEntry")
    return (
      (import.meta.env.VITE_DEV_API_ORIGIN || "https://api.tgrb.cn") +
      url.pathname.replace(/^\/__api/, "") +
      url.search
    );
  return url.toString();
}
export class ApiError extends Error {
  constructor(response) {
    super(response.resultNote || response.message || "请求失败，请稍后重试");
    Object.assign(this, response);
    this.name = "ApiError";
    this.resultNote = this.message;
  }
}
async function send(url, payload, options = {}) {
  const method = (options.method || "POST").toUpperCase();
  const multipart = payload instanceof FormData;
  const headers = {
    uid: currentUid(),
    shareId: String(readStorage("wxfid")),
    ...(!multipart ? { "Content-Type": "application/json;charset=UTF-8" } : {}),
  };
  if (method === "GET") {
    const parsed = new URL(url, location.origin);
    for (const [key, value] of Object.entries(payload || {}))
      parsed.searchParams.set(key, String(value));
    url = parsed.toString();
  }
  try {
    const response = await fetch(url, {
      method,
      headers,
      body:
        method === "GET"
          ? undefined
          : multipart
            ? payload
            : JSON.stringify(payload || {}),
      signal: options.signal,
    });
    if (!response.ok)
      throw new ApiError({
        resultNote: `服务器请求失败（${response.status}）`,
      });
    const result = await response.json();
    if (["0", "3"].includes(String(result.result))) return result.data;
    if (["2", "4", "5", "6"].includes(String(result.result))) {
      clearSession();
      window.dispatchEvent(
        new CustomEvent("session-expired", { detail: result.resultNote }),
      );
    }
    throw new ApiError(result);
  } catch (error) {
    if (!options.silent && error.name !== "AbortError")
      toast(error.resultNote || "网络错误，请重试");
    throw error;
  }
}
export function request(action, payload = {}, options = {}) {
  return send(getApiUrl(action), payload, options);
}
export function categoryRequest(action, payload = {}, options = {}) {
  return send(
    `/category-api.php?action=${encodeURIComponent(action)}`,
    payload,
    options,
  );
}
async function upload(action, input, fields = {}) {
  let file = input?.file || input?.raw || input;
  const path =
    input?.filePath ||
    input?.path ||
    input?.tempFilePath ||
    (typeof input === "string" ? input : "");
  if (!(file instanceof Blob) && path.startsWith("blob:"))
    file = await fetch(path).then((response) => response.blob());
  if (!(file instanceof Blob)) {
    toast("请选择图片");
    throw new ApiError({ resultNote: "请选择图片" });
  }
  if (action === "api/uploadAuthorAvatar" && file.size > 200 * 1024) {
    toast("图片不能超过200KB");
    throw new ApiError({ resultNote: "图片不能超过200KB" });
  }
  const data = new FormData();
  Object.entries(fields).forEach(([key, value]) => data.append(key, value));
  data.append("file", file, file.name || input?.name || "upload.png");
  return send(getApiUrl(action), data);
}
const actions = {
  login: "login",
  wechatAuthUrl: "wechatAuthUrl",
  wechatOauthLogin: "wechatOauthLogin",
  sendSms: "sendSms",
  regist: "userRegist",
  findPassword: "findPassword",
  member: "getMemberById",
  shareMerchant: "shareMerchant",
  modifyMember: "modifyMember",
  articles: "getMyArticleList",
  homeGoods: "homeGoods",
  article: "getArticleById",
  batchCheckArticles: "batchCheckArticles",
  batchPublishArticles: "batchPublishArticles",
  quickPublishSource: "quickPublishSource",
  ordinaryBatchSource: "ordinaryBatchSource",
  publishOrdinaryBatch: "publishOrdinaryBatch",
  authors: "getAuthorList",
  saveAuthor: "saveAuthor",
  deleteAuthor: "deleteAuthor",
  deleteArticle: "deleteArticle",
  sortArticle: "modifyArticleSort",
  saveOrder: "saveOrder",
  payOrder: "payOrder",
  orders: "orderList",
  sellerOrders: "mYorderList",
  balanceLogs: "getBalanceDetailList",
  incomeSummary: "myIncomeSummary",
  withdraw: "tixian",
  apply: "userApply",
  share: "shareUrl",
  children: "getMyChildrenList",
  subList: "getMySubList",
  setFencheng: "setFencheng",
  visits: "getMemberShareList",
  online: "getMemberShareOnLineList",
  recharge: "saveRechage",
  payRecharge: "payRechage",
  rechargeList: "getRechageDetailList",
  payTypes: "payTypeList",
  about: "aboutUs",
  privacy: "privacyAgreement",
  cancelAccount: "cancelAccount",
  kefu: "kefu",
  messages: "msgList",
};
export const api = Object.fromEntries(
  Object.entries(actions).map(([name, action]) => [
    name,
    (payload, options) => request(`api/${action}`, payload, options),
  ]),
);
async function categories(payload = {}, options = {}) {
  try {
    return await categoryRequest("list", payload, { ...options, silent: true });
  } catch {
    return request("api/getBiaoqianList", payload, options);
  }
}
Object.assign(api, {
  request,
  categories,
  tags: categories,
  saveArticle: (payload, options) =>
    request(
      "api/saveArticle",
      { ...payload, clientBuild: "20260812g" },
      options,
    ),
  saveCategory: (payload, options) => categoryRequest("save", payload, options),
  deleteCategory: (payload, options) =>
    categoryRequest("delete", payload, options),
  authorCategoryMap: (payload = {}) =>
    categoryRequest("authorMap", payload, { silent: true }).catch(() => ({})),
  authorProfiles: (payload = {}) =>
    categoryRequest("authorProfiles", payload, { silent: true }).catch(
      () => ({}),
    ),
  saveAuthorCategory: (payload, options) =>
    categoryRequest("saveAuthorCategory", payload, options),
  saveAuthorProfile: (payload, options) =>
    categoryRequest("saveAuthorProfile", payload, options),
  deleteAuthorCategory: (payload, options) =>
    categoryRequest("deleteAuthorCategory", payload, options),
  articleFlags: (payload = {}) =>
    categoryRequest("articleFlags", payload, { silent: true }).catch(
      () => ({}),
    ),
  saveArticleFlag: (payload, options) =>
    categoryRequest("saveArticleFlag", payload, options),
  uploadCategoryImage: (file, fields) =>
    upload("api/uploadAuthorAvatar", file, fields),
  uploadAuthorAvatar: (file, fields) =>
    upload("api/uploadAuthorAvatar", file, fields),
  uploadImage: (file, fields) => upload("api/uploadImage", file, fields),
});
